import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, updateDoc, increment, arrayUnion } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { db, storage } from './firebase';
import { getAuth } from 'firebase/auth';
import {
  FaMapMarkerAlt,
  FaMedal,
  FaStar,
  FaUserFriends,
  FaBed,
  FaCheckCircle,
  FaEye,
  FaIdBadge
} from 'react-icons/fa';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import './PGListing.css';
import apnagharLogo from './apnaghar.png';

// ---------------- Skeleton Loader ----------------
const RentSkeleton = () => (
  <div className="pg-card-wrapper">
    <div className="pg-card skeleton">
      <div className="pg-img skeleton-img" />
      <div className="pg-content">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className={`skeleton-line ${i === 0 ? 'short' : ''}`} />
        ))}
      </div>
    </div>
  </div>
);

// ---------------- Main Component ----------------
function RentListView() {
  const { type } = useParams();
  const navigate = useNavigate();
  const auth = getAuth();
  const isMounted = useRef(true);

  const [rentList, setRentList] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [occupancyFilter, setOccupancyFilter] = useState('');
  const [roomTypeFilter, setRoomTypeFilter] = useState('');
  const [filterLoading, setFilterLoading] = useState(false);

  useEffect(() => {
    isMounted.current = true;

    const fetchListings = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'rent-listings'));
        const data = await Promise.all(
          snapshot.docs.map(async (docSnap) => {
            const rent = { id: docSnap.id, ...docSnap.data(), thumbnailURL: null };

            rent.views = typeof rent.views === 'number' ? rent.views : 0;
            rent.viewedBy = Array.isArray(rent.viewedBy) ? rent.viewedBy : [];
            rent.showBooked = rent.showBooked || false;

            // ✅ Fetch only from Firebase
            const thumbnailURL = rent.thumbnailPath
              ? await getDownloadURL(ref(storage, rent.thumbnailPath)).catch(() => '')
              : '';

            // ✅ Use Firestore fields instead of Google API
            rent.thumbnailURL = thumbnailURL;
            // ✅ Fetch from Firestore fields
rent.location = rent.city || 'Assam';
rent.state = rent.state || 'Assam'; // ✅ Fetch 'state' field if exists, else fallback

            rent.nearbyRoads =
              rent.nearbyRoads?.length > 0
                ? rent.nearbyRoads
                : [{ name: 'Near University Road', distance: '0.5 km' }];

            return rent;
          })
        );

        let filtered = [];
        if (type === 'featured') filtered = data.filter((pg) => pg.isPremium);
        else if (type === 'verified') filtered = data.filter((pg) => pg.isVerified && !pg.isPremium);
        else filtered = data;

        if (isMounted.current) {
          setRentList(filtered);
          setLoading(false);
        }
      } catch (err) {
        console.error('Error fetching rent listings:', err);
        if (isMounted.current) setLoading(false);
      }
    };

    fetchListings();
    return () => {
      isMounted.current = false;
    };
  }, [type]);

  const handleCardClick = useCallback(
    async (rent) => {
      if (rent.showBooked) return;

      sessionStorage.setItem(`scrollPos-${type}`, window.scrollY);
      sessionStorage.setItem(`rent-card-${type}`, rent.id);

      const user = auth.currentUser;
      if (user && !rent.viewedBy.includes(user.uid)) {
        try {
          await updateDoc(doc(db, 'rent-listings', rent.id), {
            views: increment(1),
            viewedBy: arrayUnion(user.uid),
          });
        } catch (err) {
          console.error(err);
        }
      }
      navigate(`/rent/${rent.id}`);
    },
    [auth, navigate, type]
  );

  const filteredRents = rentList.filter(
    (rent) =>
      (!occupancyFilter || rent.occupancy?.toLowerCase() === occupancyFilter.toLowerCase()) &&
      (!roomTypeFilter || rent.roomType?.toLowerCase() === roomTypeFilter.toLowerCase())
  );

  useEffect(() => {
    if (!loading && filteredRents.length > 0) {
      const savedPos = sessionStorage.getItem(`scrollPos-${type}`);
      if (savedPos) {
        window.scrollTo({ top: parseInt(savedPos), behavior: 'auto' });
      }
    }
  }, [loading, filteredRents, type]);

  return (
    <div className="pg-section">
      <button onClick={() => navigate(-1)} className="back-btnwhy">← Back</button>
      <div className="pg-container">

        <div className="logoo-container" style={{ textAlign: 'center', marginBottom: '20px' }}>
          <img src={apnagharLogo} alt="ApkaGhr Logo" style={{ maxWidth: '180px', height: 'auto' }} />
          <h1
            className="brand-text"
            style={{
              background: 'linear-gradient(90deg, #E60056, #4126A7)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            ApkaGhr
          </h1>
        </div>

        <div className="rentlist-header" style={{ textAlign: 'center', marginBottom: '30px' }}>
          <h2
            className="rentlist-heading"
            style={{
              fontSize: '2rem',
              fontWeight: '700',
              background: 'linear-gradient(90deg, #FF4E50, #F9D423)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              marginBottom: '10px',
            }}
          >
            Hassle-Free Rentals
          </h2>
          <p
            className="rentlist-subheading"
            style={{
              fontSize: '1.1rem',
              color: '#555',
              lineHeight: '1.6',
              maxWidth: '650px',
              margin: '0 auto',
            }}
          >
            Search, find, and book your perfect home in Assam with verified listings, zero stress, and 100% broker-free experience.
          </p>
        </div>

        {/* ---------------- Filter Bar ---------------- */}
        <div className="filter-bar">
          <div className="filter-group">
            <FaUserFriends className="filter-icon" />
            <select
              value={occupancyFilter}
              onChange={(e) => {
                setFilterLoading(true);
                setOccupancyFilter(e.target.value);
                setTimeout(() => setFilterLoading(false), 300);
              }}
              className="filter-select"
            >
              <option value="">All Occupancy</option>
              <option value="family">Family</option>
              <option value="students">Students</option>
              <option value="professional">Professional</option>
              <option value="everyone">Everyone</option>
            </select>
          </div>

          <div className="filter-group">
            <FaBed className="filter-icon" />
            <select
              value={roomTypeFilter}
              onChange={(e) => {
                setFilterLoading(true);
                setRoomTypeFilter(e.target.value);
                setTimeout(() => setFilterLoading(false), 300);
              }}
              className="filter-select"
            >
              <option value="">All Room Types</option>
              <option value="1bhk">1BHK</option>
              <option value="2bhk">2BHK</option>
              <option value="3bhk">3BHK</option>
              <option value="4bhk">4BHK</option>
              <option value="5bhk">5BHK</option>
              <option value="6bhk">6BHK</option>
            </select>
          </div>

          <button className="reset-btn" onClick={() => { setOccupancyFilter(''); setRoomTypeFilter(''); }}>
            Reset Filters
          </button>
        </div>

        <h2 className="section-header-title">
          {type === 'featured' ? 'Premium Listings' : type === 'verified' ? 'Verified Listings' : 'All Rent Listings'}
        </h2>

        <div className="pg-grid">
          {(loading || filterLoading)
            ? Array.from({ length: 6 }).map((_, idx) => <RentSkeleton key={idx} />)
            : filteredRents.length === 0
              ? <p>No listings found.</p>
              : filteredRents.map((rent) => (
                  <div
                    key={rent.id}
                    id={`rent-card-${rent.id}`}
                    className={`pg-card-wrapper ${rent.showBooked ? 'booked' : ''}`}
                    onClick={() => handleCardClick(rent)}
                  >
                    {rent.showBooked && (
                      <div className="booked-overlay">
                        <div className="booked-text">🏠 Booked</div>
                        <p className="booked-subtext">No rooms available</p>
                      </div>
                    )}
                    {rent.isPremium && <div className="premium-badge"><FaMedal /> Featured</div>}

                    <div
                      className={`pg-card ${rent.isPremium ? 'premium-card' : ''} ${
                        rent.isVerified && !rent.isPremium ? 'verified-border' : ''
                      }`}
                    >
                      <LazyLoadImage
                        src={rent.thumbnailURL || 'https://dummyimage.com/400x300/cccccc/000000&text=No+Image'}
                        alt={rent.pgName || 'Rent Image'}
                        effect="blur"
                        className="pg-img"
                      />
                      <div className="pg-content">
                        <h3>
                          {rent.pgName}{' '}
                          {rent.isVerified && <FaCheckCircle className="verified-tick" />}
                        </h3>
                        <p><FaMapMarkerAlt /> {rent.location || 'Assam'}, {rent.state || 'Assam'}</p>

                        <p><FaUserFriends /> {rent.occupancy || 'N/A'}</p>
                        <p><FaBed /> {rent.roomType || 'N/A'}</p>
                        <p className="price">₹ {rent.rent || 'N/A'}/month</p>
                        <p><FaIdBadge /> Listing ID: {rent.id}</p>

                        {rent.nearbyRoads && rent.nearbyRoads.length > 0 && (
                          <div className="road-slider">
                            {rent.nearbyRoads.map((r, idx) => (
                              <div key={idx} className="road-info">
                                <FaMapMarkerAlt className="road-icon" />
                                <span>{`${r.name} • ${r.distance}`}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        <p className="rating">
                          {[1, 2, 3, 4, 5].map((n) => (
                            <FaStar
                              key={n}
                              color={n <= Math.round(rent.ratingAvg || 0) ? '#f5c518' : '#d0d0d0'}
                            />
                          ))}
                          {rent.ratingCount > 0 && <span>({rent.ratingCount})</span>} |{' '}
                          <FaEye /> {rent.views}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
        </div>
      </div>
    </div>
  );
}

export default RentListView;
